import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Navbar } from "@/components/Navbar";
import { Loader2, Lock, KeyRound } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Password change state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [changingPw, setChangingPw] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));

      if (res.data.user.must_change_password) {
        setCurrentPw(password);
        setShowChangePassword(true);
        toast.info("Please set a new password to continue");
      } else {
        toast.success("Welcome back!");
        navigate("/admin/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { toast.error("Passwords don't match"); return; }
    if (newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setChangingPw(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/auth/change-password`, { current_password: currentPw, new_password: newPw },
        { headers: { Authorization: `Bearer ${token}` } });
      const user = JSON.parse(localStorage.getItem("user"));
      user.must_change_password = false;
      localStorage.setItem("user", JSON.stringify(user));
      toast.success("Password updated successfully!");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally { setChangingPw(false); }
  };

  if (showChangePassword) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0f1117] transition-colors duration-300" data-testid="change-password-page">
        <Navbar />
        <div className="pt-32 pb-16 px-6 flex justify-center">
          <div className="w-full max-w-sm">
            <div className="text-center mb-8">
              <div className="bg-brand-gold/10 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4">
                <KeyRound className="h-6 w-6 text-brand-gold" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">Set New Password</h1>
              <p className="text-sm text-slate-500 font-body mt-1">Please change your password before continuing</p>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-4" data-testid="change-password-form">
              <div>
                <Label htmlFor="new-pw" className="font-body text-sm text-slate-700 mb-1.5 block">New Password</Label>
                <Input id="new-pw" type="password" autoComplete="new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required
                  className="font-body bg-white border-slate-200 focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 rounded-lg"
                  placeholder="Enter new password" data-testid="input-new-password" />
              </div>
              <div>
                <Label htmlFor="confirm-pw" className="font-body text-sm text-slate-700 mb-1.5 block">Confirm New Password</Label>
                <Input id="confirm-pw" type="password" autoComplete="new-password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required
                  className="font-body bg-white border-slate-200 focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 rounded-lg"
                  placeholder="Confirm new password" data-testid="input-confirm-password" />
              </div>
              <Button type="submit" disabled={changingPw}
                className="w-full bg-brand-purple hover:bg-brand-purple-hover text-white rounded-full py-3 font-body font-semibold transition-all duration-300"
                data-testid="change-password-btn">
                {changingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1117] transition-colors duration-300" data-testid="admin-login-page">
      <Navbar />
      <div className="pt-32 pb-16 px-6 flex justify-center">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="bg-brand-purple-soft rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4">
              <Lock className="h-6 w-6 text-brand-purple" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">Admin Login</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 font-body mt-1">Sign in to manage classes</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="login-email" className="font-body text-sm text-slate-700 mb-1.5 block">Email</Label>
              <Input id="login-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="font-body bg-white border-slate-200 focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 rounded-lg"
                placeholder="hello@tutorsmelbourne.com.au" data-testid="input-login-email" />
            </div>
            <div>
              <Label htmlFor="login-password" className="font-body text-sm text-slate-700 mb-1.5 block">Password</Label>
              <Input id="login-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="font-body bg-white border-slate-200 focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 rounded-lg"
                placeholder="Enter password" data-testid="input-login-password" />
            </div>
            <Button type="submit" disabled={loading}
              className="w-full bg-brand-purple hover:bg-brand-purple-hover text-white rounded-full py-3 font-body font-semibold transition-all duration-300"
              data-testid="login-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
